import { Component, type ErrorInfo, type ReactNode } from "react";
import { AxiosError } from "axios";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public override state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  private getErrorMessage(): string {
    const { error } = this.state;
    if (!error) return "An unexpected error occurred";

    if (error instanceof AxiosError) {
      if (error.response?.status === 401) {
        return "Your session has expired. Please log in again.";
      }
      if (error.response?.data) {
        const data = error.response.data as {
          detail?: string | { msg?: string }[];
        };
        if (typeof data.detail === "string") {
          return data.detail;
        }
        if (Array.isArray(data.detail)) {
          return data.detail.map((e) => e.msg || "Validation error").join(", ");
        }
      }
      return error.message || "A network error occurred";
    }

    return error.message || "An unexpected error occurred";
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  private handleGoHome = () => {
    window.location.href = "/projects";
  };

  private handleReload = () => {
    window.location.reload();
  };

  public override render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const errorMessage = this.getErrorMessage();
      const isAuthError =
        this.state.error instanceof AxiosError &&
        this.state.error.response?.status === 401;

      return (
        <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-8">
          <div className="max-w-md w-full bg-card border border-border rounded-lg p-8 shadow-sm">
            <h1 className="text-2xl font-bold text-destructive mb-4">
              Something went wrong
            </h1>
            <p className="text-foreground mb-6">{errorMessage}</p>
            <div className="flex gap-4">
              <button
                onClick={this.handleReset}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
              >
                Try again
              </button>
              {isAuthError ? (
                <button
                  onClick={() => {
                    window.location.href = "/login";
                  }}
                  className="px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/90 transition-colors"
                >
                  Go to login
                </button>
              ) : (
                <>
                  <button
                    onClick={this.handleGoHome}
                    className="px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/90 transition-colors"
                  >
                    Go home
                  </button>
                  <button
                    onClick={this.handleReload}
                    className="px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/90 transition-colors"
                  >
                    Reload page
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
